import { type Ref, useState } from 'react'
import {
	type PlaceSuggestion,
	usePlaceSuggestions
} from '#hooks/use-place-suggestions'
import {
	Combobox,
	ComboboxContent,
	ComboboxInput,
	ComboboxItem,
	ComboboxList
} from '#ui/combobox'

/** What the student picked, split the way a form of the address asks for it. */
interface PickedAddress {
	/** The street, without the number. */
	street: string | null
	number: string | null
	postalCode: string | null
	/** The municipality as Google names it. */
	locality: string | null
	/** The province code, such as "RM". */
	provinceCode: string | null
	/** Where Google puts the place, with its id; only when `withLocation` asks, null otherwise. */
	location: {
		latitude: number
		longitude: number
		googlePlaceId: string
	} | null
}

const part = (
	components: google.maps.places.AddressComponent[],
	type: string,
	short = false
) => {
	const found = components.find((item) => item.types.includes(type))
	return (short ? found?.shortText : found?.longText) ?? null
}

const TRAILING_NUMBER_REGEX = /,\s*(\d[\w/]*)$/

/** The house number at the end of the main text of a suggestion, such as "4" or "10B" from "Via Roma, 10B". */
const numberOf = (mainText: string | undefined) =>
	mainText?.match(TRAILING_NUMBER_REGEX)?.[1] ?? null

/**
 * An address typed as free text, with the suggestions of Google Places under it (the Places API New,
 * limited to Italy). The text is the student's own, whatever Google says: suggestions only help, and
 * without a key or when Google cannot be loaded this is a plain field. Picking one asks Google for the
 * parts of that place, in one request that closes the search session, and hands them to the form.
 * The words the student types reach Google: the privacy notice says so, and the list says whose it is.
 */
function AddressSuggest({
	apiKey,
	language,
	value,
	onValueChange,
	onPick,
	withLocation = false,
	attribution,
	id,
	name,
	placeholder,
	onBlur,
	ref,
	...aria
}: {
	apiKey: string | undefined
	language: string
	value: string
	onValueChange: (text: string) => void
	onPick: (address: PickedAddress) => void
	/** Also asks Google for where the place is: for an address the page keeps a position of, not one of a person. */
	withLocation?: boolean
	/** The line that credits Google under the suggestions ("Suggerimenti di Google"). */
	attribution: string
	id?: string
	name?: string
	placeholder?: string
	onBlur?: () => void
	ref?: Ref<HTMLInputElement>
	'aria-invalid'?: boolean
	'aria-describedby'?: string
}) {
	const [open, setOpen] = useState(false)
	// Google cannot be reached: the field stays a plain one.
	const { suggestions, ask, close, endSession } = usePlaceSuggestions({
		apiKey,
		language,
		primaryTypes: ['street_address', 'route', 'premise']
	})

	const pick = async (item: PlaceSuggestion | null) => {
		if (!item) {
			return
		}
		close()
		setOpen(false)
		try {
			const place = item.prediction.toPlace()
			await place.fetchFields({
				fields: withLocation
					? ['addressComponents', 'id', 'location']
					: ['addressComponents']
			})
			const components = place.addressComponents ?? []
			onPick({
				street: part(components, 'route'),
				// A number Google does not know as a place ("Via Santa Maria in Gradi, 4" comes
				// back as the street alone) is still in the suggestion picked.
				number:
					part(components, 'street_number') ??
					numberOf(item.prediction.mainText?.text),
				postalCode: part(components, 'postal_code'),
				locality:
					part(components, 'locality') ??
					part(components, 'administrative_area_level_3'),
				provinceCode: part(
					components,
					'administrative_area_level_2',
					true
				),
				location:
					withLocation && place.id && place.location
						? {
								latitude: place.location.lat(),
								longitude: place.location.lng(),
								googlePlaceId: place.id
							}
						: null
			})
		} catch {
			// The place cannot be read: what the student typed stays as it is.
		} finally {
			endSession()
		}
	}

	return (
		<Combobox<PlaceSuggestion>
			filter={null}
			inputValue={value}
			isItemEqualToValue={(a, b) => a.value === b.value}
			items={suggestions}
			itemToStringLabel={(item) => item.label}
			itemToStringValue={(item) => item.value}
			onInputValueChange={(text, details) => {
				if (details.reason === 'input-change') {
					onValueChange(text)
					setOpen(true)
					ask(text)
				}
			}}
			onOpenChange={setOpen}
			onValueChange={pick}
			open={open && suggestions.length > 0}
			value={null}
		>
			<ComboboxInput
				{...aria}
				autoComplete='off'
				id={id}
				name={name}
				onBlur={onBlur}
				placeholder={placeholder}
				ref={ref}
				showTrigger={false}
			/>
			<ComboboxContent>
				<ComboboxList>
					{(item: PlaceSuggestion) => (
						<ComboboxItem key={item.value} value={item}>
							{item.label}
						</ComboboxItem>
					)}
				</ComboboxList>
				<p className='border-border border-t px-3 py-2 text-muted-foreground text-xs'>
					{attribution}
				</p>
			</ComboboxContent>
		</Combobox>
	)
}

export { AddressSuggest }
export type { PickedAddress }
